import type { ChordId } from '../types';
import { getAudioContext } from './webAudioEngine';

export interface CelestialChordDef {
  id: ChordId;
  label: string;
  chordName: string;
  color: string;
  notes: number[];
}

export const CELESTIAL_CHORDS: CelestialChordDef[] = [
  { id: 'do', label: 'Do', chordName: 'C', color: '#E53935', notes: [261.63, 329.63, 392.0] },
  { id: 're', label: 'Re', chordName: 'Dm', color: '#FB8C00', notes: [293.66, 349.23, 440.0] },
  { id: 'mi', label: 'Mi', chordName: 'Em', color: '#FDD835', notes: [329.63, 392.0, 493.88] },
  { id: 'fa', label: 'Fa', chordName: 'F', color: '#43A047', notes: [349.23, 440.0, 523.25] },
  { id: 'sol', label: 'Sol', chordName: 'G', color: '#29B6F6', notes: [392.0, 493.88, 587.33] },
  { id: 'la', label: 'La', chordName: 'Am', color: '#7E57C2', notes: [440.0, 523.25, 659.25] },
  { id: 'si', label: 'Si', chordName: 'B°', color: '#EC407A', notes: [493.88, 587.33, 698.46] },
];

export const CHORD_IDS: ChordId[] = CELESTIAL_CHORDS.map((c) => c.id);

export const CHORD_COLORS: Record<ChordId, string> = Object.fromEntries(
  CELESTIAL_CHORDS.map((c) => [c.id, c.color]),
) as Record<ChordId, string>;

export const CHORD_LABELS: Record<ChordId, string> = Object.fromEntries(
  CELESTIAL_CHORDS.map((c) => [c.id, `${c.label} ${c.chordName}`]),
) as Record<ChordId, string>;

export function chordFrequencies(id: ChordId): number[] {
  return CELESTIAL_CHORDS.find((c) => c.id === id)?.notes ?? [];
}

/** Piano eléctrico Rhodes — ataque inmediato, decay rápido (no cola de pad). */
const ATTACK_SEC = 0.001;
const STRIKE_DECAY_SEC = 0.045;
const PEAK_LEVEL = 0.5;
const RELEASE_SEC = 0.18;
const HARMONY_BASE_GAIN = 2.35;
const PER_NOTE_GAIN = 0.68;
const STROKE_REVERB_SEND = 0.16;
const HOLD_REVERB_SEND = 0.28;
/** Duración audible fija: la grilla puede ser larga pero el Rhodes decae rápido. */
const MIN_STROKE_SEC = 0.14;
const MAX_STROKE_SEC = 0.44;

function strokeAudioDuration(holdSec: number): number {
  return Math.max(MIN_STROKE_SEC, Math.min(holdSec, MAX_STROKE_SEC));
}

interface VoiceHandle {
  nodes: AudioNode[];
  stop: () => void;
}

interface HoldVoice {
  master: GainNode;
  handles: VoiceHandle[];
  releasing: boolean;
}

function createHallImpulse(ctx: AudioContext, seconds = 1.4, decay = 2.0): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** decay;
    }
  }
  return impulse;
}

/** Una tine de Rhodes: FM + filtro + ataque casi instantáneo. */
function spawnEPTine(
  ctx: AudioContext,
  freq: number,
  destination: GainNode,
  when: number,
  totalDuration: number,
  velocity = 1,
): VoiceHandle {
  const perNote = ctx.createGain();
  perNote.gain.value = PER_NOTE_GAIN * velocity;
  perNote.connect(destination);

  const carrier = ctx.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(freq, when);

  const mod = ctx.createOscillator();
  mod.type = 'sine';
  mod.frequency.setValueAtTime(freq * 4, when);

  const modEnv = ctx.createGain();
  const modDepth = freq * 7.5 * velocity;
  modEnv.gain.setValueAtTime(modDepth, when);
  modEnv.gain.exponentialRampToValueAtTime(0.001, when + 0.18);

  mod.connect(modEnv);
  modEnv.connect(carrier.frequency);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.65;
  filter.frequency.setValueAtTime(6200, when);
  filter.frequency.exponentialRampToValueAtTime(1200, when + 0.24);

  const amp = ctx.createGain();
  const peak = PEAK_LEVEL * velocity;
  const bodyLevel = Math.max(peak * 0.32, 0.006);
  const audibleEnd = when + totalDuration;
  const strikeEnd = when + ATTACK_SEC + STRIKE_DECAY_SEC;

  amp.gain.setValueAtTime(0, when);
  amp.gain.linearRampToValueAtTime(peak, when + ATTACK_SEC);
  amp.gain.exponentialRampToValueAtTime(bodyLevel, strikeEnd);
  amp.gain.exponentialRampToValueAtTime(0.001, audibleEnd);

  carrier.connect(filter);
  filter.connect(amp);
  amp.connect(perNote);

  const warm = ctx.createOscillator();
  warm.type = 'triangle';
  warm.frequency.setValueAtTime(freq * 1.004, when);
  const warmAmp = ctx.createGain();
  warmAmp.gain.setValueAtTime(0, when);
  warmAmp.gain.linearRampToValueAtTime(peak * 0.18, when + ATTACK_SEC);
  warmAmp.gain.exponentialRampToValueAtTime(0.001, audibleEnd);
  warm.connect(warmAmp);
  warmAmp.connect(perNote);

  const stopAt = audibleEnd + 0.04;
  carrier.start(when);
  mod.start(when);
  warm.start(when);
  carrier.stop(stopAt);
  mod.stop(stopAt);
  warm.stop(stopAt);

  const nodes: AudioNode[] = [carrier, mod, warm, modEnv, filter, amp, warmAmp, perNote];
  return {
    nodes,
    stop: () => {
      nodes.forEach((n) => {
        try {
          n.disconnect();
        } catch {
          /* noop */
        }
      });
    },
  };
}

function spawnHoldTine(
  ctx: AudioContext,
  freq: number,
  destination: GainNode,
  when: number,
  velocity = 1,
): VoiceHandle {
  const perNote = ctx.createGain();
  perNote.gain.value = PER_NOTE_GAIN * velocity;
  perNote.connect(destination);

  const carrier = ctx.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(freq, when);

  const mod = ctx.createOscillator();
  mod.type = 'sine';
  mod.frequency.setValueAtTime(freq * 4, when);

  const modEnv = ctx.createGain();
  modEnv.gain.setValueAtTime(freq * 6.5 * velocity, when);
  modEnv.gain.exponentialRampToValueAtTime(0.001, when + 0.38);

  mod.connect(modEnv);
  modEnv.connect(carrier.frequency);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.75;
  filter.frequency.setValueAtTime(5200, when);
  filter.frequency.exponentialRampToValueAtTime(1200, when + 0.5);

  const amp = ctx.createGain();
  const peak = PEAK_LEVEL * velocity;
  const bodyLevel = Math.max(peak * 0.32, 0.004);
  const strikeEnd = when + ATTACK_SEC + STRIKE_DECAY_SEC;

  amp.gain.setValueAtTime(0, when);
  amp.gain.linearRampToValueAtTime(peak, when + ATTACK_SEC);
  amp.gain.exponentialRampToValueAtTime(bodyLevel, strikeEnd);

  carrier.connect(filter);
  filter.connect(amp);
  amp.connect(perNote);

  carrier.start(when);
  mod.start(when);

  const nodes: AudioNode[] = [carrier, mod, modEnv, filter, amp, perNote];
  return {
    nodes,
    stop: () => {
      [carrier, mod].forEach((o) => {
        try {
          o.stop();
        } catch {
          /* noop */
        }
      });
      nodes.forEach((n) => {
        try {
          n.disconnect();
        } catch {
          /* noop */
        }
      });
    },
  };
}

function spawnChordVoices(
  ctx: AudioContext,
  frequencies: number[],
  destination: GainNode,
  when: number,
  totalDuration: number,
  hold = false,
): VoiceHandle[] {
  const vel = 1.18 / Math.max(1, frequencies.length * 0.26);
  return frequencies.map((freq) =>
    hold
      ? spawnHoldTine(ctx, freq, destination, when, vel)
      : spawnEPTine(ctx, freq, destination, when, totalDuration, vel),
  );
}

class CelestialPadEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private dry: GainNode | null = null;
  private wet: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private ready = false;
  private harmonyOutputGain = HARMONY_BASE_GAIN;
  private holdVoices = new Map<string, HoldVoice>();
  private strokeEndTimers: ReturnType<typeof setTimeout>[] = [];

  setHarmonyMixGain(gain: number): void {
    this.harmonyOutputGain = Math.max(0, gain);
  }

  async unlock(): Promise<boolean> {
    this.ctx = await getAudioContext();
    if (!this.ready) {
      this.initGraph();
      this.ready = true;
    }
    return this.ctx.state === 'running';
  }

  private initGraph(): void {
    const ctx = this.ctx!;
    this.master = ctx.createGain();
    this.master.gain.value = 1;

    this.dry = ctx.createGain();
    this.dry.gain.value = 0.96;

    this.wet = ctx.createGain();
    this.wet.gain.value = 0.32;

    this.reverb = ctx.createConvolver();
    this.reverb.buffer = createHallImpulse(ctx);
    this.reverb.normalize = true;

    this.dry.connect(this.master);
    this.reverb.connect(this.wet);
    this.wet.connect(this.master);
    this.master.connect(ctx.destination);
  }

  private connectVoice(gain: GainNode, reverbSend = STROKE_REVERB_SEND): void {
    gain.connect(this.dry!);
    const send = this.ctx!.createGain();
    send.gain.value = reverbSend;
    gain.connect(send);
    send.connect(this.reverb!);
  }

  startHold(id: ChordId): void {
    if (!this.ctx || !this.ready) return;
    const key = `hold:${id}`;
    const existing = this.holdVoices.get(key);
    if (existing && !existing.releasing) return;

    existing?.handles.forEach((h) => h.stop());
    this.holdVoices.delete(key);

    const ctx = this.ctx;
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = this.harmonyOutputGain;
    this.connectVoice(master, HOLD_REVERB_SEND);

    const handles = spawnChordVoices(ctx, chordFrequencies(id), master, now, 0, true);
    this.holdVoices.set(key, { master, handles, releasing: false });
  }

  stopHold(id: ChordId): void {
    const key = `hold:${id}`;
    const voice = this.holdVoices.get(key);
    if (!voice || voice.releasing || !this.ctx) return;

    voice.releasing = true;
    const now = this.ctx.currentTime;
    const release = RELEASE_SEC;

    voice.handles.forEach((h) => {
      const amp = h.nodes.find((n) => n instanceof GainNode && n !== voice.master) as GainNode | undefined;
      if (amp?.gain) {
        const current = Math.max(amp.gain.value, 0.001);
        amp.gain.cancelScheduledValues(now);
        amp.gain.setValueAtTime(current, now);
        amp.gain.exponentialRampToValueAtTime(0.001, now + release);
      }
    });

    window.setTimeout(() => {
      voice.handles.forEach((h) => h.stop());
      voice.master.disconnect();
      this.holdVoices.delete(key);
    }, release * 1000 + 80);
  }

  /** Golpe programado — ataque inmediato, duración = holdSec. */
  scheduleStroke(
    id: ChordId,
    when: number,
    holdSec: number,
    frequencies?: number[],
  ): void {
    if (!this.ctx || !this.ready) return;

    const ctx = this.ctx;
    const freqs = frequencies?.length ? frequencies : chordFrequencies(id);
    const playAt = Math.max(when, ctx.currentTime);
    const duration = strokeAudioDuration(holdSec);
    const master = ctx.createGain();
    master.gain.value = this.harmonyOutputGain;
    this.connectVoice(master);

    const handles = spawnChordVoices(ctx, freqs, master, playAt, duration);
    const stopAt = playAt + duration + 0.08;

    const timer = window.setTimeout(() => {
      handles.forEach((h) => h.stop());
      master.disconnect();
      this.strokeEndTimers = this.strokeEndTimers.filter((t) => t !== timer);
    }, Math.max(0, (stopAt - ctx.currentTime) * 1000 + 60));

    this.strokeEndTimers.push(timer);
  }

  stopAll(): void {
    for (const id of [...this.holdVoices.keys()]) {
      const chordId = id.replace('hold:', '') as ChordId;
      this.stopHold(chordId);
    }
    this.strokeEndTimers.forEach(clearTimeout);
    this.strokeEndTimers = [];
  }

  previewStroke(id: ChordId, holdSec = 0.55, frequencies?: number[]): void {
    if (!this.ctx || !this.ready) return;
    this.scheduleStroke(id, this.ctx.currentTime, holdSec, frequencies);
  }

  previewFrequencies(frequencies: number[], holdSec = 0.55): void {
    if (!this.ctx || !this.ready || frequencies.length === 0) return;
    const playAt = this.ctx.currentTime;
    const duration = strokeAudioDuration(holdSec);
    const master = this.ctx.createGain();
    master.gain.value = this.harmonyOutputGain;
    this.connectVoice(master);
    const handles = spawnChordVoices(this.ctx, frequencies, master, playAt, duration);
    const stopAt = playAt + duration + 0.08;
    const timer = window.setTimeout(() => {
      handles.forEach((h) => h.stop());
      master.disconnect();
      this.strokeEndTimers = this.strokeEndTimers.filter((t) => t !== timer);
    }, Math.max(0, (stopAt - this.ctx!.currentTime) * 1000 + 60));
    this.strokeEndTimers.push(timer);
  }
}

export const celestialPadEngine = new CelestialPadEngine();

export async function unlockCelestialPad(): Promise<boolean> {
  return celestialPadEngine.unlock();
}

export function previewCelestialChord(
  id: ChordId,
  holdSec?: number,
  frequencies?: number[],
): void {
  void unlockCelestialPad().then((ok) => {
    if (!ok) return;
    celestialPadEngine.previewStroke(id, holdSec, frequencies);
  });
}

export function previewChordFrequencies(frequencies: number[], holdSec = 0.55): void {
  void unlockCelestialPad().then((ok) => {
    if (!ok) return;
    celestialPadEngine.previewFrequencies(frequencies, holdSec);
  });
}

export function scheduleCelestialChords(
  events: Array<{ chord: ChordId; when: number; holdSec: number; frequencies?: number[] }>,
  startAt: number,
): void {
  for (const evt of events) {
    celestialPadEngine.scheduleStroke(
      evt.chord,
      startAt + evt.when,
      evt.holdSec,
      evt.frequencies,
    );
  }
}

export { ATTACK_SEC, RELEASE_SEC, STRIKE_DECAY_SEC };
